import { Api } from "@/apiClient/Api";
import { Patent } from "@/apiClient/Patent";
import { Dashboard } from "@/apiClient/Dashboard";
import { Whistleblower } from "@/apiClient/Whistleblower";
import { Regulatory } from "@/apiClient/Regulatory";
import { LegalDiagnosis } from "@/apiClient/LegalDiagnosis";
import { LegalChat } from "@/apiClient/LegalChat";
import { DownloadDocument } from "@/apiClient/DownloadDocument";
import { DocumentDrafting } from "@/apiClient/DocumentDrafting";
import { DemandLetter } from "@/apiClient/DemandLetter";
import { ContractTypes } from "@/apiClient/ContractTypes";
import { ContractReview } from "@/apiClient/ContractReview";
import { ContentType } from "@/apiClient/http-client";
import { API_BASE_URL } from "@/config";
import {
  getAccessToken,
  createAuthHeaders,
  isSessionValid,
} from "@/lib/auth-utils";
import { supabase } from "@/lib/supabase";
import { getRefreshToken } from "@/lib/token-store";

// Global variable to store the session expired callback
let onSessionExpired: (() => void) | null = null;

// Function to set the session expired callback
export function setSessionExpiredCallback(callback: () => void) {
  onSessionExpired = callback;
}

export class AuthenticatedApiClient {
  private api: Api;
  private patent: Patent;
  private dashboard: Dashboard;
  private whistleblower: Whistleblower;
  private regulatory: Regulatory;
  private legalDiagnosis: LegalDiagnosis;
  private legalChat: LegalChat;
  private downloadDocument: DownloadDocument;
  private documentDrafting: DocumentDrafting;
  private demandLetter: DemandLetter;
  private contractTypes: ContractTypes;
  private contractReview: ContractReview;
  public baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
    const config = {
      baseUrl: this.baseUrl,
      baseApiParams: {
        secure: true, // Always require authentication
      },
      securityWorker: async () => {
        // SecurityWorker called - getting auth headers

        try {
          // Check if session is valid before making request
          const isValid = await isSessionValid().catch(() => false);
          // Session validation completed

          if (!isValid) {
            console.warn("⚠️ Session is not valid, attempting to refresh...");

            // Try to refresh the session
            const {
              data: { session },
              error: refreshError,
            } = await supabase.auth.refreshSession();

            if (refreshError || !session?.access_token) {
              console.error("🔐 Failed to refresh session:", refreshError);
              // Trigger session expired callback if available
              if (onSessionExpired) {
                onSessionExpired();
              }
              throw new Error("Session expired and could not be refreshed");
            }

            // Session refreshed successfully
          }

          // Get auth headers with current token (should be fresh now)
          const authHeaders = await createAuthHeaders();
          const refresh = getRefreshToken();
          // Auth headers generated

          const result = {
            headers: {
              ...authHeaders,
              ...(refresh ? { "X-Refresh-Token": refresh } : {}),
              // Don't set Content-Type for FormData requests - let the browser set it with boundary
            },
          };

          return result;
        } catch (error) {
          console.error("🔐 Error in securityWorker:", error);
          // Return empty headers on error, let the request fail naturally
          return {
            headers: {},
          };
        }
      },
    };

    this.api = new Api(config);
    this.patent = new Patent(config);
    this.dashboard = new Dashboard(config);
    this.whistleblower = new Whistleblower(config);
    this.regulatory = new Regulatory(config);
    this.legalDiagnosis = new LegalDiagnosis(config);
    this.legalChat = new LegalChat(config);
    this.downloadDocument = new DownloadDocument(config);
    this.documentDrafting = new DocumentDrafting(config);
    this.demandLetter = new DemandLetter(config);
    this.contractTypes = new ContractTypes(config);
    this.contractReview = new ContractReview(config);
  }

  // Main API
  get apiClient() {
    return this.api;
  }

  // Patent API
  get patentClient() {
    return this.patent;
  }

  // Dashboard API
  get dashboardClient() {
    return this.dashboard;
  }

  // Whistleblower API
  get whistleblowerClient() {
    return this.whistleblower;
  }

  // Regulatory API
  get regulatoryClient() {
    return this.regulatory;
  }

  // Legal Diagnosis API
  get legalDiagnosisClient() {
    return this.legalDiagnosis;
  }

  // Legal Chat API
  get legalChatClient() {
    return this.legalChat;
  }

  // Download Document API
  get downloadDocumentClient() {
    return this.downloadDocument;
  }

  // Document Drafting API
  get documentDraftingClient() {
    return this.documentDrafting;
  }

  // Custom Document API
  get customDocumentClient() {
    return this.api;
  }

  // Demand Letter API
  get demandLetterClient() {
    return this.demandLetter;
  }

  // Contract Types API
  get contractTypesClient() {
    return this.contractTypes;
  }

  // Contract Review API
  get contractReviewClient() {
    return this.contractReview;
  }

  /**
   * Generic POST method using the shared security worker and configuration.
   */
  async post(path: string, body: any, options: any = {}) {
    // Determine the type for the request (json, formData, etc.)
    // Note: HttpClient's formData formatter expects a plain object, not a FormData instance
    const type = options.type || (body instanceof FormData || body?.file ? ContentType.FormData : ContentType.Json);
    
    const response = await (this.api as any).request({
        path: path,
        method: "POST",
        body: body,
        secure: true,
        type: type,
        format: "json",
        ...options
    });
    return response;
  }

  /**
   * Generic GET method.
   */
  async get(path: string, params?: any) {
    const response = await (this.api as any).request({
        path: path,
        method: "GET",
        query: params,
        secure: true,
        format: "json"
    });
    return response;
  }

  /**
   * Generic PUT method.
   */
  async put(path: string, body: any, options: any = {}) {
    const type = options.type || (body instanceof FormData || body?.file ? ContentType.FormData : ContentType.Json);
    const response = await (this.api as any).request({
        path: path,
        method: "PUT",
        body: body,
        secure: true,
        type: type,
        format: "json",
        ...options
    });
    return response;
  }

  /**
   * Generic DELETE method.
   */
  async delete(path: string, params?: any) {
    const response = await (this.api as any).request({
        path: path,
        method: "DELETE",
        query: params,
        secure: true,
        format: "json"
    });
    return response;
  }
}

// Create a singleton instance
export const authenticatedApiClient = new AuthenticatedApiClient();
